// messageSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  sendMessage,
  getConversations,
  getConversationMessages,
  searchMessages,
  pinMessage,
  forwardMessage,
  deleteMessage,
  getUserPresence,
  getAllUsers // Import the new API call
} from '../../api.js'

// =============================================
// ASYNC THUNKS FOR MESSAGE OPERATIONS
// =============================================

// Send message thunk
export const sendMessageThunk = createAsyncThunk(
  'messages/sendMessage',
  async (messageData, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log('📤 Sending message data:', messageData)

      // Log FormData contents if it's FormData
      if (messageData instanceof FormData) {
        console.log('📎 FormData entries:')
        for (let [key, value] of messageData.entries()) {
          console.log(`  ${key}:`, value)
        }
      }

      const response = await sendMessage(messageData)
      const message = response.data
      const conversationId = response.conversationId

      if (conversationId) {
        // Check if this was sent from a temp conversation
        const state = getState()
        const tempConversation = state.messages.conversations.find(conv => {
          if (!conv._id.startsWith('temp_')) return false

          // Handle both FormData and regular object
          const receiverId =
            messageData instanceof FormData
              ? messageData.get('receiverId')
              : messageData.receiverId

          return conv.participants.some(p => p._id === receiverId)
        })

        if (tempConversation) {
          dispatch(
            cleanupTempConversation({
              tempConversationId: tempConversation._id,
              realConversationId: conversationId
            })
          )
          dispatch(getConversationsThunk())
        }

        dispatch(addMessageToConversation({ conversationId, message }))
      }
      return response
    } catch (error) {
      console.error('❌ Send message error:', error)
      console.error('❌ Error response:', error.response?.data)
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to send message'
      )
    }
  }
)

// Get conversations thunk
export const getConversationsThunk = createAsyncThunk(
  'messages/getConversations',
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await getConversations(page, limit)
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch conversations')
    }
  }
)

// Get conversation messages thunk
export const getConversationMessagesThunk = createAsyncThunk(
  'messages/getConversationMessages',
  async ({ conversationId, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      const response = await getConversationMessages(
        conversationId,
        page,
        limit
      )
      return { conversationId, ...response }
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch conversation messages'
      )
    }
  }
)

// Search messages thunk
export const searchMessagesThunk = createAsyncThunk(
  'messages/searchMessages',
  async ({ query, conversationId = null }, { rejectWithValue }) => {
    try {
      const response = await searchMessages(query, conversationId)
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to search messages')
    }
  }
)

// Pin message thunk
export const pinMessageThunk = createAsyncThunk(
  'messages/pinMessage',
  async ({ messageId, pin = true }, { rejectWithValue }) => {
    try {
      const response = await pinMessage(messageId, pin)
      return { messageId, pin, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to pin/unpin message')
    }
  }
)

// Forward message thunk
export const forwardMessageThunk = createAsyncThunk(
  'messages/forwardMessage',
  async (
    { messageId, receiverIds = [], groupIds = [] },
    { rejectWithValue }
  ) => {
    try {
      const response = await forwardMessage(messageId, {
        receiverIds,
        groupIds
      })
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to forward message')
    }
  }
)

// Delete message thunk
export const deleteMessageThunk = createAsyncThunk(
  'messages/deleteMessage',
  async ({ messageId, deleteType }, { rejectWithValue }) => {
    try {
      const response = await deleteMessage(messageId, deleteType)
      return { messageId, deleteType, ...response }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete message')
    }
  }
)

// Get user presence thunk
export const getUserPresenceThunk = createAsyncThunk(
  'messages/getUserPresence',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await getUserPresence(userId)
      return { userId, ...response }
    } catch (error) {
      if (error?.status !== 404 && error?.response?.status !== 404) {
        return rejectWithValue(error.message || 'Failed to fetch user presence')
      }
      return { userId, isOnline: false, lastSeen: null }
    }
  }
)

// Get all users thunk
export const getAllUsersThunk = createAsyncThunk(
  'messages/getAllUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllUsers()
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch all users')
    }
  }
)

// =============================================
// MESSAGE SLICE
// =============================================

const initialState = {
  conversations: [],
  conversationMessages: {},
  searchResults: [],
  userPresence: {},
  pinnedMessages: [],
  allUsers: [], // New state for all users
  loading: {
    sendMessage: false,
    getConversations: false,
    getConversationMessages: false,
    searchMessages: false,
    pinMessage: false,
    forwardMessage: false,
    deleteMessage: false,
    getUserPresence: false,
    getAllUsers: false // New loading state
  },
  error: {
    sendMessage: null,
    getConversations: null,
    getConversationMessages: null,
    searchMessages: null,
    pinMessage: null,
    forwardMessage: null,
    deleteMessage: null,
    getUserPresence: null,
    getAllUsers: null // New error state
  },
  currentConversationId: null,
  currentUserId: localStorage.getItem('userId') || null, // Store current user ID for comparing messages
  pagination: {
    conversations: { page: 1, hasMore: true, totalPages: 1 },
    messages: {}
  }
}

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setCurrentConversation: (state, action) => {
      state.currentConversationId = action.payload
    },
    clearSearchResults: state => {
      state.searchResults = []
    },
    clearError: (state, action) => {
      const errorType = action.payload
      if (errorType && state.error[errorType]) {
        state.error[errorType] = null
      }
    },
    addMessageToConversation: (state, action) => {
      const { conversationId, message } = action.payload
      // Ensure the conversation exists before adding the message
      if (!state.conversationMessages[conversationId]) {
        state.conversationMessages[conversationId] = {
          messages: [],
          pagination: {}
        }
      }

      // Check if message already exists to avoid duplicates
      const messageExists = state.conversationMessages[
        conversationId
      ].messages.some(msg => msg._id === message._id)

      if (!messageExists) {
        // Add message to the messages array (at the end for chronological order)
        state.conversationMessages[conversationId].messages.push(message)

        // Update lastMessage and lastActivity for the conversation in the conversations list
        const conversationIndex = state.conversations.findIndex(
          conv => conv._id === conversationId
        )
        if (conversationIndex !== -1) {
          state.conversations[conversationIndex].lastMessage = message
          state.conversations[conversationIndex].lastActivity = message.sentAt

          // Update unread count for the receiver ONLY if message is not from current user
          if (message.sender._id !== state.currentUserId) {
            // Handle both array and number format for unreadCount
            if (
              Array.isArray(state.conversations[conversationIndex].unreadCount)
            ) {
              const unreadEntry = state.conversations[
                conversationIndex
              ].unreadCount.find(u => u.user.toString() === state.currentUserId)
              if (unreadEntry) {
                unreadEntry.count += 1
              } else {
                state.conversations[conversationIndex].unreadCount.push({
                  user: state.currentUserId,
                  count: 1
                })
              }
            } else {
              // Legacy format - convert to number
              if (
                typeof state.conversations[conversationIndex].unreadCount ===
                'number'
              ) {
                state.conversations[conversationIndex].unreadCount += 1
              } else {
                state.conversations[conversationIndex].unreadCount = 1
              }
            }
          }

          // Sort conversations to bring the one with new message to top
          state.conversations.sort(
            (a, b) => new Date(b.lastActivity) - new Date(a.lastActivity)
          )
        }
      }
    },
    updateMessageStatus: (state, action) => {
      const { conversationId, messageId, status } = action.payload
      if (state.conversationMessages[conversationId]) {
        const message = state.conversationMessages[
          conversationId
        ].messages.find(msg => msg._id === messageId)
        if (message) {
          message.status = status
        }
      }
    },
    // Add a new action for typing indicators
    setUserTyping: (state, action) => {
      const { conversationId, userId, isTyping } = action.payload
    },
    addTempConversation: (state, action) => {
      const tempConversation = action.payload
      // Check if conversation doesn't already exist
      const exists = state.conversations.find(
        conv =>
          conv.conversationType === 'direct' &&
          conv.participants.some(
            participant =>
              participant._id === tempConversation.participants[0]._id
          )
      )

      if (!exists) {
        // Add to the beginning of conversations list
        state.conversations.unshift(tempConversation)
        // Initialize empty messages array for this conversation
        state.conversationMessages[tempConversation._id] = {
          messages: [],
          pagination: { page: 1, hasMore: false, totalPages: 1, total: 0 }
        }
      }
    },
    cleanupTempConversation: (state, action) => {
      const { tempConversationId, realConversationId } = action.payload
      // Remove temp conversation
      state.conversations = state.conversations.filter(
        conv => conv._id !== tempConversationId
      )
      // Remove temp conversation messages
      delete state.conversationMessages[tempConversationId]
      // Update current conversation ID if it was the temp one
      if (state.currentConversationId === tempConversationId) {
        state.currentConversationId = realConversationId
      }
    },
    resetMessageState: () => initialState,

    markConversationRead: (state, action) => {
      const { conversationId, userId, unreadCount = 0 } = action.payload
      const conversation = state.conversations.find(
        conv => conv._id === conversationId
      )
      if (conversation) {
        // Handle array format (preferred)
        if (Array.isArray(conversation.unreadCount)) {
          const unreadEntry = conversation.unreadCount.find(
            u => u.user && u.user.toString() === userId
          )
          if (unreadEntry) {
            unreadEntry.count = unreadCount
          } else {
            conversation.unreadCount.push({ user: userId, count: unreadCount })
          }
        } else {
          // Handle legacy number format
          conversation.unreadCount = unreadCount
        }
      }
    },

    updateMessageDeliveryStatus: (state, action) => {
      const { messageId, userId, status } = action.payload

      // Update message in all conversations
      Object.keys(state.conversationMessages).forEach(conversationId => {
        const message = state.conversationMessages[
          conversationId
        ].messages.find(msg => msg._id === messageId)
        if (message) {
          if (status === 'delivered' && !message.deliveredTo) {
            message.deliveredTo = []
          }
          if (status === 'read' && !message.readBy) {
            message.readBy = []
          }

          // Update delivery status
          if (status === 'delivered' && message.deliveredTo) {
            const existingDelivery = message.deliveredTo.find(
              d => d.user === userId
            )
            if (!existingDelivery) {
              message.deliveredTo.push({
                user: userId,
                deliveredAt: new Date()
              })
            }
          }

          // Update read status
          if (status === 'read' && message.readBy) {
            const existingRead = message.readBy.find(r => r.user === userId)
            if (!existingRead) {
              message.readBy.push({ user: userId, readAt: new Date() })
            }
          }
        }
      })
    }
  },
  extraReducers: builder => {
    // Send message cases
    builder
      .addCase(sendMessageThunk.pending, state => {
        state.loading.sendMessage = true
        state.error.sendMessage = null
      })
      .addCase(sendMessageThunk.fulfilled, (state, action) => {
        state.loading.sendMessage = false
        // Message is already added by addMessageToConversation reducer in the thunk
      })
      .addCase(sendMessageThunk.rejected, (state, action) => {
        state.loading.sendMessage = false
        state.error.sendMessage = action.payload
      })

    // Get conversations cases - FIXED TO HANDLE CORRECT RESPONSE STRUCTURE
    builder
      .addCase(getConversationsThunk.pending, state => {
        state.loading.getConversations = true
        state.error.getConversations = null
      })
      .addCase(getConversationsThunk.fulfilled, (state, action) => {
        state.loading.getConversations = false
        const { conversations = [], pagination = {} } = action.payload

        if ((pagination.page || 1) === 1) {
          state.conversations = conversations
        } else {
          // Ensure no duplicates when loading more conversations
          const existingConversationIds = new Set(
            state.conversations.map(conv => conv._id)
          )
          const newConversations = conversations.filter(
            conv => !existingConversationIds.has(conv._id)
          )
          state.conversations = [...state.conversations, ...newConversations]
        }

        state.pagination.conversations = {
          page: pagination.page || 1,
          hasMore: pagination.hasMore || false,
          totalPages: pagination.totalPages || 1,
          total: pagination.total || 0
        }
      })
      .addCase(getConversationsThunk.rejected, (state, action) => {
        state.loading.getConversations = false
        state.error.getConversations = action.payload
      })

    // Get conversation messages cases - FIXED TO HANDLE CORRECT RESPONSE STRUCTURE
    builder
      .addCase(getConversationMessagesThunk.pending, state => {
        state.loading.getConversationMessages = true
        state.error.getConversationMessages = null
      })
      .addCase(getConversationMessagesThunk.fulfilled, (state, action) => {
        state.loading.getConversationMessages = false
        const {
          conversationId,
          messages = [],
          pagination = {}
        } = action.payload

        if ((pagination.page || 1) === 1) {
          state.conversationMessages[conversationId] = {
            messages,
            pagination
          }
        } else {
          const existingMessages =
            state.conversationMessages[conversationId]?.messages || []
          // Prepend new messages to existing ones to maintain chronological order when loading older messages
          state.conversationMessages[conversationId] = {
            messages: [...messages, ...existingMessages],
            pagination
          }
        }

        state.pagination.messages[conversationId] = {
          page: pagination.page || 1,
          hasMore: pagination.hasMore || false,
          totalPages: pagination.totalPages || 1,
          total: pagination.total || 0
        }
      })
      .addCase(getConversationMessagesThunk.rejected, (state, action) => {
        state.loading.getConversationMessages = false
        state.error.getConversationMessages = action.payload
      })

    // Search messages cases
    builder
      .addCase(searchMessagesThunk.pending, state => {
        state.loading.searchMessages = true
        state.error.searchMessages = null
      })
      .addCase(searchMessagesThunk.fulfilled, (state, action) => {
        state.loading.searchMessages = false
        state.searchResults = action.payload.messages || []
      })
      .addCase(searchMessagesThunk.rejected, (state, action) => {
        state.loading.searchMessages = false
        state.error.searchMessages = action.payload
      })

    // Pin message cases
    builder
      .addCase(pinMessageThunk.pending, state => {
        state.loading.pinMessage = true
        state.error.pinMessage = null
      })
      .addCase(pinMessageThunk.fulfilled, (state, action) => {
        state.loading.pinMessage = false
        const { messageId, pin } = action.payload

        // Update message in conversations
        Object.keys(state.conversationMessages).forEach(conversationId => {
          const message = state.conversationMessages[
            conversationId
          ].messages.find(msg => msg._id === messageId)
          if (message) {
            message.isPinned = pin
          }
        })

        // Update pinned messages list
        if (pin) {
          if (!state.pinnedMessages.find(msg => msg._id === messageId)) {
            state.pinnedMessages.push(action.payload.message)
          }
        } else {
          state.pinnedMessages = state.pinnedMessages.filter(
            msg => msg._id !== messageId
          )
        }
      })
      .addCase(pinMessageThunk.rejected, (state, action) => {
        state.loading.pinMessage = false
        state.error.pinMessage = action.payload
      })

    // Forward message cases
    builder
      .addCase(forwardMessageThunk.pending, state => {
        state.loading.forwardMessage = true
        state.error.forwardMessage = null
      })
      .addCase(forwardMessageThunk.fulfilled, state => {
        state.loading.forwardMessage = false
      })
      .addCase(forwardMessageThunk.rejected, (state, action) => {
        state.loading.forwardMessage = false
        state.error.forwardMessage = action.payload
      })

    // Delete message cases
    builder
      .addCase(deleteMessageThunk.pending, state => {
        state.loading.deleteMessage = true
        state.error.deleteMessage = null
      })
      .addCase(deleteMessageThunk.fulfilled, (state, action) => {
        state.loading.deleteMessage = false
        const { messageId, deleteType } = action.payload

        if (deleteType === 'onlyForYou') {
          // Remove message only from local state
          Object.keys(state.conversationMessages).forEach(conversationId => {
            state.conversationMessages[conversationId].messages =
              state.conversationMessages[conversationId].messages.filter(
                msg => msg._id !== messageId
              )
          })
        } else if (deleteType === 'forEveryone') {
          // Replace message content with deleted message indicator
          Object.keys(state.conversationMessages).forEach(conversationId => {
            const messageIndex = state.conversationMessages[
              conversationId
            ].messages.findIndex(msg => msg._id === messageId)
            if (messageIndex !== -1) {
              state.conversationMessages[conversationId].messages[
                messageIndex
              ] = {
                ...state.conversationMessages[conversationId].messages[
                  messageIndex
                ],
                content: 'This message was deleted',
                messageType: 'deleted',
                isDeleted: true
              }
            }
          })
        }

        // Remove from pinned messages
        state.pinnedMessages = state.pinnedMessages.filter(
          msg => msg._id !== messageId
        )
      })
      .addCase(deleteMessageThunk.rejected, (state, action) => {
        state.loading.deleteMessage = false
        state.error.deleteMessage = action.payload
      })

    // Get user presence cases
    builder
      .addCase(getUserPresenceThunk.pending, state => {
        state.loading.getUserPresence = true
        state.error.getUserPresence = null
      })
      .addCase(getUserPresenceThunk.fulfilled, (state, action) => {
        state.loading.getUserPresence = false
        const { userId, isOnline, lastSeen } = action.payload
        state.userPresence[userId] = { isOnline, lastSeen }
      })
      .addCase(getUserPresenceThunk.rejected, (state, action) => {
        if (
          action.error?.message?.includes('404') ||
          action.payload?.includes('not found')
        ) {
          state.loading.getUserPresence = false
          return
        }
        state.loading.getUserPresence = false
        state.error.getUserPresence = action.payload
      })

    // Get all users cases
    builder
      .addCase(getAllUsersThunk.pending, state => {
        state.loading.getAllUsers = true
        state.error.getAllUsers = null
      })
      .addCase(getAllUsersThunk.fulfilled, (state, action) => {
        state.loading.getAllUsers = false
        state.allUsers = action.payload.users || []
      })
      .addCase(getAllUsersThunk.rejected, (state, action) => {
        state.loading.getAllUsers = false
        state.error.getAllUsers = action.payload
      })
  }
})

export const {
  setCurrentConversation,
  clearSearchResults,
  clearError,
  addMessageToConversation,
  updateMessageStatus,
  updateMessageDeliveryStatus,
  setUserTyping,
  resetMessageState,
  addTempConversation,
  cleanupTempConversation,
  markConversationRead
} = messageSlice.actions

export default messageSlice.reducer
